import React, { useEffect, useState } from "react";
import { Typography, Box, TextField, Button, Stack } from "@mui/material";
import { styled } from "@mui/system";
import { useSelector } from "react-redux";
import {
  approvePreReport,
  getPreReportFeedback,
  sendPreReportFeedback,
} from "../../../../api/preReportFeedback";
import { downloadProject, fetchProjectPdf } from "../../../../api/handleProjects";
import MySnackbar from "../../MySnackBar";
import PreReportFeedbackHistory from "../../PreReportFeedback/PreReportFeedbackHistory";

// Estilos
const GroupReviewContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  marginTop: theme.spacing(2),
  padding: theme.spacing(3),
  // border: "1px solid #ccc",
  borderRadius: "8px",
  // boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  backgroundColor: "#ffffff",
  width: "100%",
}));

const PdfPreviewBox = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "500px", // Mantén altura fija
  border: "1px solid #ccc",
  borderRadius: "4px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "#f9f9f9",
  marginTop: theme.spacing(2),
}));

const DownloadButton = styled(Button)(({ theme }) => ({
  width: "100%", // Hacer que el botón sea igual de largo
  marginTop: theme.spacing(2), // Espaciado superior
  backgroundColor: "#0072C6",
  color: "#ffffff",
  "&:hover": {
    backgroundColor: "#005B9A",
  },
  borderRadius: "4px",
}));

const GroupReview = ({ group }) => {
  const [comment, setComment] = useState("");
  const [pdfUrl, setPdfUrl] = useState(null);
  const [history, setHistory] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const period = useSelector((state) => state.period);
  const user = useSelector((state) => state.user);

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    status: "",
  });

  const handleSnackbarClose = () => {
    setNotification({ ...notification, open: false });
  };

  const handleCommentChange = (event) => {
    setComment(event.target.value);
  };

  const downloadFile = async () => {
    try {
      await downloadProject(group.id, user, period.id, 'initial-project', group.group_number);
    } catch (error) {
      console.error("Error al descargar el archivo:", error);
    }
  };

  const loadHistory = async () => {
    try {
      setHistory(await getPreReportFeedback(user, group.id));
    } catch (error) {
      console.error("Error al cargar las devoluciones:", error);
    }
  };

  // Dos acciones distintas, a propósito. Antes había un solo botón que
  // mandaba el comentario por mail y marcaba el anteproyecto como aprobado en
  // la misma jugada, así que "pedí correcciones" y "aprobé" quedaban iguales
  // en la base. Ahora la devolución no aprueba, y la aprobación queda
  // registrada con fecha y autor. El backend guarda la entrada aunque el mail
  // no salga, y lo dice en `detail`.
  const submit = async (action, okStatus) => {
    setSubmitting(true);
    try {
      const result = await action();
      setHistory(result);
      setComment("");
      // Que la lista del dashboard refleje la aprobación sin recargar
      group.pre_report_approved = result.pre_report_approved;
      group.pre_report_reviewed_at = result.pre_report_reviewed_at;
      setNotification({
        open: true,
        message: result.detail,
        status: result.sent ? okStatus : "warning",
      });
    } catch (error) {
      console.error("Error al registrar la devolución:", error);
      const detail = error?.response?.data?.detail;
      setNotification({
        open: true,
        message:
          [401, 409, 422].includes(error?.response?.status) && detail
            ? typeof detail === "string"
              ? detail
              : "El mensaje no puede estar vacío"
            : "No se pudo registrar la devolución. Inténtalo de nuevo.",
        status: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Aprobar es del revisor (y del admin). El tutor solo deja devoluciones;
  // el backend lo rechaza igual, esto es para no mostrar un boton que falla.
  const canApprove =
    user?.role === "admin" || (group?.reviewer_id && group.reviewer_id === user?.id);

  const handleSendFeedback = () =>
    submit(() => sendPreReportFeedback(user, group.id, comment), "success");

  const handleApprove = () =>
    submit(() => approvePreReport(user, group.id, comment), "success");

  // Función para cargar el PDF en la previsualización
  const loadPdfPreview = async () => {
    try {
      const url = await fetchProjectPdf(group.id, user, period.id, 'initial');
      setPdfUrl(url); // Guarda la URL en el estado
    } catch (error) {
      console.error("Error al cargar la previsualización del PDF:", error);
    }
  };

  useEffect(() => {
    loadPdfPreview();
    loadHistory();
  }, [group.id, user, period]);

  return (
    <GroupReviewContainer>
      <Typography variant="h4" align="center" gutterBottom>
        Anteproyecto
      </Typography>

      <PdfPreviewBox>
        {pdfUrl ? (
          <iframe
            src={pdfUrl}
            title="Previsualización del PDF"
            width="100%"
            height="100%"
            style={{ border: "none" }}
          />
        ) : (
          <Typography>Cargando ...</Typography>
        )}
      </PdfPreviewBox>

      {/* Botón para descargar el PDF */}
      <DownloadButton variant="contained" onClick={downloadFile}>
        Descargar PDF
      </DownloadButton>

      <Box sx={{ width: "100%", marginTop: 3 }}>
        <PreReportFeedbackHistory history={history} />
      </Box>

      <Box
        sx={{
          width: "100%",
          marginTop: 2,
        }}
      >
        <Typography variant="h6" mb={1}>
          Nueva devolución
        </Typography>
        <TextField
          multiline
          rows={4}
          value={comment}
          onChange={handleCommentChange}
          variant="outlined"
          placeholder="Escribe tu comentario aquí..."
          sx={{ mb: 2, width: "100%" }}
        />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Button
            variant="contained"
            onClick={handleSendFeedback}
            color="primary"
            disabled={submitting || !comment.trim()}
            sx={{ flex: 1, borderRadius: "4px" }}
          >
            Enviar devolución
          </Button>
          {canApprove && (
            <Button
              variant="outlined"
              onClick={handleApprove}
              color="success"
              disabled={submitting}
              sx={{ flex: 1, borderRadius: "4px" }}
            >
              {history?.pre_report_approved ? "Volver a aprobar" : "Aprobar anteproyecto"}
            </Button>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          {canApprove
            ? "La devolución no aprueba el anteproyecto. Aprobar lo marca como aprobado y el mensaje es opcional. En los dos casos el grupo recibe un mail."
            : "La devolución le llega al grupo y al revisor por mail. La aprobación del anteproyecto la hace el revisor."}
        </Typography>
      </Box>
      <MySnackbar
        open={notification.open}
        handleClose={handleSnackbarClose}
        message={notification.message}
        status={notification.status}
      />
    </GroupReviewContainer>
  );
};

export default GroupReview;
